import React, { useCallback, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import AuthContext from "../auth/AuthContext";
import PostList from "../components/PostList";
import { getCategoryPosts, getPostTags } from "../utils/apiClient";
import useDocumentTitle from "../hooks/useDocumentTitle";

function Categories() {
  useDocumentTitle("Kategorier");
  const { user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTag = searchParams.get("tag") || "";

  const [tags, setTags] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loadingTags, setLoadingTags] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [error, setError] = useState("");

  const loadTags = useCallback(async () => {
    setLoadingTags(true);
    try {
      const data = await getPostTags();
      setTags(Array.isArray(data) ? data : []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Failed to load tags");
    } finally {
      setLoadingTags(false);
    }
  }, [user?.user_uuid]);

  const loadPosts = useCallback(async () => {
    if (!tags.length || !selectedTag) {
      setPosts([]);
      return;
    }
    setLoadingPosts(true);
    setError("");
    try {
      const data = await getCategoryPosts({ tag: selectedTag });
      setPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Failed to load posts");
    } finally {
      setLoadingPosts(false);
    }
  }, [selectedTag, tags.length, user?.user_uuid]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  useEffect(() => {
    if (!loadingTags) loadPosts();
  }, [loadPosts, loadingTags]);

  const selectTag = (tag) => {
    const next = new URLSearchParams(searchParams);
    if (selectedTag === tag) next.delete("tag");
    else next.set("tag", tag);
    setSearchParams(next, { replace: true });
  };

  return (
    <Box sx={{ py: { xs: 1, sm: 2 } }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 2 }}>
        Kategorier
      </Typography>

      {loadingTags ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : tags.length ? (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 3 }}>
          {tags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              clickable
              color={selectedTag === tag ? "primary" : "default"}
              variant={selectedTag === tag ? "filled" : "outlined"}
              onClick={() => selectTag(tag)}
            />
          ))}
        </Stack>
      ) : (
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Ingen tags endnu. Tilføj kommaseparerede tags, når du gemmer et indlæg.
        </Typography>
      )}

      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      {!tags.length ? null : !selectedTag ? (
        <Typography color="text.secondary">Vælg en kategori</Typography>
      ) : loadingPosts ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <PostList posts={posts} emptyMessage={`Ingen indlæg med tagget “${selectedTag}”.`} />
      )}
    </Box>
  );
}

export default Categories;
